/**
 * Artifact Repository - Platform Persistence layer.
 *
 * Public interface for Blueprint artifact persistence. All reads and writes
 * go through this module. No caller touches Supabase directly for artifacts.
 *
 * Write path:
 *   writeBlueprint()
 *     → supabase.rpc("write_blueprint_artifact")  [atomic: version + both INSERTs]
 *     → reads RETURNING (version, created_at)
 *     → assembles ArtifactEnvelope from authoritative database values
 *     → constructArtifact(envelope, content) → Blueprint
 *     → retries up to MAX_VERSION_RETRIES times on UNIQUE constraint violation
 *
 * Read path:
 *   readBlueprintByVersion / readLatestBlueprint / readBlueprintLineage
 *     → joined select on pi_artifact_envelopes + blueprint_artifact_contents
 *     → rowToBlueprint() reassembles typed Blueprint from stored JSONB
 *
 * Responsibility boundary:
 *   This module owns: RPC invocation, retry logic, RETURNING assembly,
 *   constructArtifact() call, error translation, public repository interface.
 *   Database layer owns: version arithmetic, timestamp generation, atomicity.
 *   Governance layer owns: artifact identity semantics, type contracts.
 */

import { supabase } from "@/integrations/supabase/client";
import type { ArtifactEnvelope } from "@/types/platformArtifactTypes";
import type { BlueprintContent } from "@/core/blueprint/blueprintContentTypes";
import type { Blueprint } from "@/intelligence/architecturePITypes";
import { constructArtifact } from "@/platform/artifactConstructor";
import { logger } from "@/system/logger";
import { toJson } from "@/integrations/supabase/json";

const MAX_VERSION_RETRIES = 3;
const UNIQUE_VIOLATION_CODE = "23505";

// ─── Private helpers ──────────────────────────────────────────────────────────

type EnvelopeWithContent = {
  version: number;
  created_at: string;
  blueprint_artifact_contents: {
    display_name: string;
    product_form: string;
    domain: string;
    modules: unknown;
    pages: unknown;
    data_models: unknown;
    roles: unknown;
    workflows: unknown;
    integrations: unknown;
  } | null;
};

function rowToBlueprint(projectId: string, row: EnvelopeWithContent): Blueprint | null {
  const c = row.blueprint_artifact_contents;
  if (!c) return null;

  const envelope: ArtifactEnvelope = {
    projectId,
    artifactType: "blueprint",
    version:      row.version,
    createdAt:    row.created_at,
  };

  const content: BlueprintContent = {
    displayName:  c.display_name,
    productForm:  c.product_form as BlueprintContent["productForm"],
    domain:       c.domain as BlueprintContent["domain"],
    modules:      c.modules as BlueprintContent["modules"],
    pages:        c.pages as BlueprintContent["pages"],
    dataModels:   c.data_models as BlueprintContent["dataModels"],
    roles:        c.roles as BlueprintContent["roles"],
    workflows:    c.workflows as BlueprintContent["workflows"],
    integrations: c.integrations as BlueprintContent["integrations"],
  };

  return constructArtifact(envelope, content);
}

// ─── Write ────────────────────────────────────────────────────────────────────

/**
 * Persist a new Blueprint artifact version atomically.
 *
 * The RPC function assigns the next contiguous version via MAX+1 inside the
 * same transaction that inserts both rows. On UNIQUE constraint violation
 * (concurrent writers landing on the same version), this function retries
 * up to MAX_VERSION_RETRIES times before throwing.
 */
export async function writeBlueprint(
  projectId: string,
  content: BlueprintContent,
): Promise<Blueprint> {
  let attempt = 0;

  while (attempt < MAX_VERSION_RETRIES) {
    const { data, error } = await supabase.rpc("write_blueprint_artifact", {
      p_project_id:   projectId,
      p_display_name: content.displayName,
      p_product_form: content.productForm,
      p_domain:       content.domain,
      p_modules:      toJson(content.modules),
      p_pages:        toJson(content.pages),
      p_data_models:  toJson(content.dataModels),
      p_roles:        toJson(content.roles),
      p_workflows:    toJson(content.workflows),
      p_integrations: content.integrations,
    });

    if (!error) {
      const row = (data as Array<{ version: number; created_at: string }>)[0];
      const envelope: ArtifactEnvelope = {
        projectId,
        artifactType: "blueprint",
        version:      row.version,
        createdAt:    row.created_at,
      };
      return constructArtifact(envelope, content);
    }

    if ((error as { code?: string }).code === UNIQUE_VIOLATION_CODE) {
      attempt++;
      logger.warn("Blueprint version conflict, retrying", { projectId, attempt });
      continue;
    }

    logger.error("writeBlueprint RPC failed", { projectId, error });
    throw new Error(`writeBlueprint failed: ${error.message}`);
  }

  throw new Error(`writeBlueprint exhausted ${MAX_VERSION_RETRIES} retries for project ${projectId}`);
}

// ─── Read ─────────────────────────────────────────────────────────────────────

/**
 * Read a specific Blueprint version. Returns null if the version does not exist.
 */
export async function readBlueprintByVersion(
  projectId: string,
  version: number,
): Promise<Blueprint | null> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, blueprint_artifact_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "blueprint")
    .eq("version", version)
    .single();

  if (error) {
    if (error.code === "PGRST116") return null; // no rows
    logger.error("readBlueprintByVersion failed", { projectId, version, error });
    throw new Error(`readBlueprintByVersion failed: ${error.message}`);
  }

  return rowToBlueprint(projectId, data as unknown as EnvelopeWithContent);
}

/**
 * Read the latest Blueprint version for a project. Returns null if none exists.
 */
export async function readLatestBlueprint(projectId: string): Promise<Blueprint | null> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, blueprint_artifact_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "blueprint")
    .order("version", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    logger.error("readLatestBlueprint failed", { projectId, error });
    throw new Error(`readLatestBlueprint failed: ${error.message}`);
  }

  if (!data) return null;
  return rowToBlueprint(projectId, data as unknown as EnvelopeWithContent);
}

/**
 * Read all Blueprint versions for a project in ascending version order.
 * Returns an empty array if no blueprints exist for the project.
 */
export async function readBlueprintLineage(projectId: string): Promise<Blueprint[]> {
  const { data, error } = await supabase
    .from("pi_artifact_envelopes")
    .select("version, created_at, blueprint_artifact_contents (*)")
    .eq("project_id", projectId)
    .eq("artifact_type", "blueprint")
    .order("version", { ascending: true });

  if (error) {
    logger.error("readBlueprintLineage failed", { projectId, error });
    throw new Error(`readBlueprintLineage failed: ${error.message}`);
  }

  return (data as unknown as EnvelopeWithContent[])
    .map(row => rowToBlueprint(projectId, row))
    .filter((b): b is Blueprint => b !== null);
}
